import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, filter, Subscription } from 'rxjs';

import { DropdownTypesEnum } from '../../../../Common/Forms/enums/dropdown-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { SearchCriteriaSortFieldEnum } from '../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ReportSeriesMentionRequestGeneratorOutput } from '../../../../Common/Reports/models/report-series-mention-request-generator-output.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { MentionContentCustomizationEnum } from '@Common/Mentions/enums/mention-content-customization.enum';

import { MADChartOptionsEnum } from '@Monitor/Shared/enums/mad-chart-options.enum';
import { TargetingRhpContextsService } from '@Common/TargetingShared/services/targeting-rhp-contexts.service';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { DialogIoModel } from '@Core/Dialogs/models/dialog-io.model';
import { MentionDetailsTakeOverPanelComponent } from '@Common/Mentions/components/MentionDetailsTakeOverPanel/mention-details-take-over-panel.component';
import { MentionCardClickedEvent } from '@Common/Reports/components/ChartDrillDownDialog/chart-drill-down-dialog.component';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { SentimentModel } from '@Monitor/Shared/models/sentiment.model';
import { MentionsService } from '@Monitor/Core/services/mentions.service';
import { ManageMentionOrganizationDialogComponent } from '@Common/Mentions/components/ManageMentionOrganization/manage-mention-organization-dialog.component';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { TranslateMentionsService } from '@Common/Mentions/services/translate-mentions.service';
import { MentionCardComponent } from '@Common/Mentions/components/MentionCard/mention-card.component';
import { SnapDomStyleOverride } from '@Social/Intelligence/services/social-chart-export.service';

// applied while the block is exported as an image: hide the view all button, pull the expand toggles in
export const topMentionsMADExportStyleOverrides: SnapDomStyleOverride[] = [
    { selector: '.agpr-topMentionsMad-viewAllButton', style: { display: 'none' } },
    { selector: '.agpr-mention-additionalInfo-expandToggle', style: { right: '5px' } }
];


@Component({
    selector: 'agpr-top-mentions-mad-chart',
    templateUrl: './top-mentions-mad-chart.component.html',
    styleUrls: ['./top-mentions-mad-chart.component.scss'],
    standalone: false
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class TopMentionsMADChart implements OnInit, OnDestroy {

    readonly messageSeverities = messageSeverityEnum;
    readonly dropdownTypes = DropdownTypesEnum;
    readonly agprComponentStyles = AgprComponentStyleEnum;
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly mentionContentTypes = MentionContentCustomizationEnum;
    readonly metricOptions: DropdownOptionModel[] = [
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.IMPRESSIONS}`, MADChartOptionsEnum.IMPRESSIONS),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.AVE}`, MADChartOptionsEnum.AVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.DOMAIN_AUTHORITY}`, MADChartOptionsEnum.DOMAIN_AUTHORITY)
    ];

    readonly sentimentOptions: DropdownOptionModel[] = [
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.POSITIVE}`, MADChartOptionsEnum.POSITIVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.NEGATIVE}`, MADChartOptionsEnum.NEGATIVE)
    ];
    readonly sentimentsToChartOptionsMap = new Map<SearchCriteriaQuerySentimentsEnum, MADChartOptionsEnum>([
        [SearchCriteriaQuerySentimentsEnum.positive, MADChartOptionsEnum.POSITIVE],
        [SearchCriteriaQuerySentimentsEnum.neutral, MADChartOptionsEnum.NEGATIVE]
    ]);
    readonly maxConcurrentTranslation: number = 3;

    @Input() id: string = '';
    @Input() get series(): ReportSeriesModel {
        return this._series;
    }
    @Input() hasSentimentDropdown: boolean = false;
    @Input() enableFocus: boolean = true;
    @Input() showTranslate: boolean = true;
    @Output() seriesChange: EventEmitter<{newSeries: ReportSeriesModel; metric?: MADChartOptionsEnum; sentiment?: MADChartOptionsEnum}> = new EventEmitter();
    @Output() openDrilldown: EventEmitter<void> = new EventEmitter<void>();
    public options: { label: string; value: ReportSeriesMentionRequestGeneratorOutput }[] = [];

    @ViewChildren('mentionCard') mentionCards: QueryList<MentionCardComponent>;

    seriesMentionSubscription: Subscription;
    mentions: Mention[] = null;

    set series(value: ReportSeriesModel) {
        this._series = value;
        this.seriesMentionSubscription?.unsubscribe();
        this.seriesMentionSubscription = this.series?.mentionProvider.subscribe(
            mentions => {
                this.mentions = mentions;
            }
        );
    }
    protected _series: ReportSeriesModel = null;
    selectedMetric: DropdownOptionModel;
    selectedSentiment: DropdownOptionModel;
    sortField: SearchCriteriaSortFieldEnum;
    overlayContainer: Element;
    openAiPermission: boolean = false;
    private subscriptions: Subscription[] = [];
    userLanguage: string;
    concurrentTranslation: number = 0;

    constructor(
        private targetingRhpContextsService: TargetingRhpContextsService,
        private coreDialogService: CoreDialogService,
        private injector: Injector,
        private mentionsServive: MentionsService,
        private currentUserService: CurrentUserStateService,
        protected translateService: TranslateMentionsService,
    ) {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.currentUserService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
            this.translateService.getLanguage().subscribe((language) => this.userLanguage = language),
            this.translateService.getConcurrentTranslation().subscribe(
                (concurrentTranslation) => this.concurrentTranslation = concurrentTranslation
            )
        );
    }

    ngOnInit(): void {
        this.selectedMetric = this.metricOptions.find(opt => opt.value === MADChartOptionsEnum.IMPRESSIONS);
        this.selectedSentiment = this.sentimentOptions.find(opt =>
            opt.value === this.sentimentsToChartOptionsMap.get(this.series.mentions.filters.searchCriteria.query.sentiments?.[0]?.id)
        );
        this.sortField = SearchCriteriaSortFieldEnum.audience_reach;
    }

    ngOnDestroy(): void {}

    metricChange(metric: MADChartOptionsEnum) {
        const newSeries = this.series.clone();

        switch (metric) {
            case MADChartOptionsEnum.IMPRESSIONS:
                this.sortField = SearchCriteriaSortFieldEnum.audience_reach;
                break;
            case MADChartOptionsEnum.AVE:
                this.sortField = SearchCriteriaSortFieldEnum.ave;
                break;
            case MADChartOptionsEnum.DOMAIN_AUTHORITY:
                this.sortField = SearchCriteriaSortFieldEnum.domainAuthority;
                break;
        }
        newSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        newSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: this.sortField,
                pageSize: 5
            };
        };

        this.seriesChange.emit({ newSeries: newSeries, metric: metric });
    }

    sentimentChange(sentiment: MADChartOptionsEnum) {
        const newSeries = this.series.clone();

        switch (sentiment) {
            case MADChartOptionsEnum.POSITIVE:
                newSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
                break;
            case MADChartOptionsEnum.NEGATIVE:
                newSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
                break;
        }

        this.seriesChange.emit({ newSeries: newSeries, sentiment: sentiment });
    }

    mentionCardClicked(event: MentionCardClickedEvent) {
        if (!event || !event.mention) {
            return;
        }

        if (event.mention.author?.uuid) {
            this.targetingRhpContextsService.setFullProfileContactById(event.mention.author.uuid);
        } else {
            this.targetingRhpContextsService.clearContact();
        }
        if (event.mention.publication?.uuid) {
            this.targetingRhpContextsService.setFullProfileOutletById(event.mention.publication.uuid);
        } else {
            this.targetingRhpContextsService.clearOutlet();
        }

        this.mentionsServive.setFocusedMention(event.mention);

        const io: DialogIoModel = {
            inputs: {
                focusTab: event.eventType,
                fromReports: true
            },
            outputs: {}
        };

        this.coreDialogService.open(MentionDetailsTakeOverPanelComponent, io, this.injector);
    }

    viewAll(): void {
        this.openDrilldown.emit();
    }

    openManageMentionOrganizationDialog(mention: Mention, event: any) {
        event.stopPropagation();
        this.coreDialogService.open(ManageMentionOrganizationDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                mention
            },
            outputs: {
                dialogClose: (newMention: Mention) => {
                    if (newMention) {
                        const {folderIds = [], tagIds = []} = newMention;
                        mention.folderIds = folderIds;
                        mention.tagIds = tagIds;
                    }
                }
            }
        });
    }

    revertTranslate(mention: Mention) {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            this.translateService.setDefaultTextAlign(mention);
            mention.showTranslated = false;
            mention.translation = null;
        }
    }

    translate(mention: Mention) {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            const mentionCard = this.mentionCards.find(element => {
                return element.mention.id === mention.id;
            });

            if (mentionCard) {
                this.translateService.setupChatAI(mention, null, true, true);
            }
        }
    }

}
